import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { authApi } from '../api/axios';
import { isTokenExpired, parseJwt } from '../utils/tokenUtils';
import { clearDocumentsCache } from '../components/documents/hooks/useFetchDocuments';
import { refreshAccessTokenSingleFlight } from '../api/tokenRefreshCoordinator';
import { prefetchAllWorkflowPolicies } from '../config/workflowPolicyCache';
import { primaryPermissionCode } from '../config/workflowRoles';

export const AuthContext = createContext(null);
const LOGIN_REDIRECT_DELAY_MS = 1000;
const MUST_CHANGE_PASSWORD_KEY = 'must_change_password';

function persistMustChangePassword(flag) {
  try {
    if (flag) {
      sessionStorage.setItem(MUST_CHANGE_PASSWORD_KEY, '1');
    } else {
      sessionStorage.removeItem(MUST_CHANGE_PASSWORD_KEY);
    }
  } catch {
    // sessionStorage может быть недоступен (приватный режим)
  }
}

function readMustChangePassword() {
  try {
    return sessionStorage.getItem(MUST_CHANGE_PASSWORD_KEY) === '1';
  } catch {
    return false;
  }
}

// Функция для получения данных пользователя из токена
const getUserFromToken = (token) => {
  if (!token) {
    return null;
  }

  try {
    const decoded = parseJwt(token);
    if (!decoded) {
      return null;
    }

    // Проверяем обязательные поля
    if (!decoded.profile_uid) {
      return null;
    }

    if (!decoded.profile_type_code) {
      return null;
    }
    
    const rawCodes = decoded.permission_codes;
    const permission_codes = Array.isArray(rawCodes)
      ? [...rawCodes]
      : rawCodes != null
        ? [String(rawCodes)]
        : [];
    // Сам факт профиля типа `system_console` = право работать в консоли:
    // иначе при отсутствии явного permission в БД оператор ловит 403 на фронте
    // ещё до того, как бэкенд успеет что-то сказать. Бэкенд-сторона
    // `require_system_console` делает такую же проверку для симметрии.
    if (decoded.profile_type_code === 'system_console' && !permission_codes.includes('system_console')) {
      permission_codes.push('system_console');
    }
    return {
      profile_uid: decoded.profile_uid,
      profile_type_code: decoded.profile_type_code,
      permission_code: primaryPermissionCode(permission_codes, decoded.permission_code),
      permission_codes,
      account_id: decoded.account_uid,
      must_change_password: Boolean(decoded.must_change_password) || readMustChangePassword(),
    };
  } catch (error) {
    void error;
    return null;
  }
};

// Функция для проверки прав через /api/auth/me
const checkPermissions = async () => {
  const response = await authApi.get('/me');
  return response.data;
};

export function AuthProvider({ children }) {
  // Получаем сохраненные значения из localStorage или используем текущее время
  const initialLastTokenCheck = parseInt(localStorage.getItem('lastTokenCheck') || Date.now().toString());
  const initialLastPermissionCheck = parseInt(localStorage.getItem('lastPermissionCheck') || Date.now().toString());
  const initialLastRedirectTime = parseInt(localStorage.getItem('lastRedirectTime') || Date.now().toString());
  
  // Инициализируем пользователя сразу из localStorage при создании контекста
  const [user, setUser] = useState(() => {
    const token = localStorage.getItem('token');
    if (token && !isTokenExpired(token)) {
      const userData = getUserFromToken(token);
      if (userData) {
        return {
          ...userData,
          token
        };
      }
    }
    return null;
  });
  
  // Синхронизация токенов между вкладками
  useEffect(() => {
    // Используем BroadcastChannel для синхронизации между вкладками
    let tokenBroadcastChannel = null;
    try {
      tokenBroadcastChannel = new BroadcastChannel('token_sync');
      tokenBroadcastChannel.onmessage = (e) => {
        if (e.data.type === 'token_updated') {
          const { token } = e.data;
          if (token) {
            const userData = getUserFromToken(token);
            if (userData) {
              setUser({
                ...userData,
                token
              });
            }
          }
        }
      };
    } catch (err) {
      void err;
    }
    
    const handleStorageChange = (e) => {
      // Если токен обновился в другой вкладке
      if (e.key === 'token' && e.newValue && e.newValue !== e.oldValue) {
        const userData = getUserFromToken(e.newValue);
        if (userData) {
          setUser({
            ...userData,
            token: e.newValue
          });
        }
      }
      
      // Если токен удален в другой вкладке
      if (e.key === 'token' && !e.newValue && e.oldValue) {
        setUser(null);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      if (tokenBroadcastChannel) {
        tokenBroadcastChannel.close();
      }
    };
  }, []);
  const [loading, setLoading] = useState(() => {
    // Если пользователь восстановлен при инициализации, сразу завершаем загрузку
    const token = localStorage.getItem('token');
    if (token && !isTokenExpired(token)) {
      const userData = getUserFromToken(token);
      if (userData) {
        return false;
      }
    }
    return true;
  });
  const [lastPermissionCheck, setLastPermissionCheck] = useState(initialLastPermissionCheck);
  const [lastRedirectTime, setLastRedirectTime] = useState(initialLastRedirectTime);
  const [lastTokenCheck, setLastTokenCheck] = useState(initialLastTokenCheck);
  
  // Используем useRef для отслеживания актуального состояния loading
  const loadingRef = useRef(loading);
  const redirectTimeoutRef = useRef(null);
  const refreshTokenSilentlyRef = useRef(() => Promise.resolve(false));
  const logoutRef = useRef(() => Promise.resolve());

  const clearRedirectTimeout = useCallback(() => {
    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
      redirectTimeoutRef.current = null;
    }
  }, []);
  
  // Обновляем ref при изменении loading
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  // Сохраняем значения в localStorage при изменении
  useEffect(() => {
    localStorage.setItem('lastTokenCheck', lastTokenCheck.toString());
  }, [lastTokenCheck]);
  
  useEffect(() => {
    localStorage.setItem('lastPermissionCheck', lastPermissionCheck.toString());
  }, [lastPermissionCheck]);
  
  useEffect(() => {
    localStorage.setItem('lastRedirectTime', lastRedirectTime.toString());
  }, [lastRedirectTime]);

  // Функция для проверки необходимости обновления прав
  const shouldCheckPermissions = () => {
    const now = Date.now();
    // Проверяем права не чаще чем раз в минуту
    return now - lastPermissionCheck > 60000;
  };

  // Функция для обновления данных пользователя (GET /me). Ответ может содержать permission_codes без permission_code.
  // useCallback([]): checkPermissions объявлена на уровне модуля и не замыкает state — ссылка стабильна и не устаревает;
  // пустой массив зависимостей нужен, чтобы refreshUserFromMe не менялся каждый рендер (иначе useEffect в ProfileDetails зацикливается).
  const updateUserData = useCallback(async () => {
    try {
      const userData = await checkPermissions();
      const profile_uid = userData.profile_uid;
      const permission_codes = Array.isArray(userData.permission_codes)
        ? [...userData.permission_codes]
        : [];
      // Симметрично getUserFromToken: system_console-профиль получает permission
      // `system_console` автоматически — иначе RouteGuard отдаст 403.
      if (userData.profile_type_code === 'system_console' && !permission_codes.includes('system_console')) {
        permission_codes.push('system_console');
      }
      const permission_code = primaryPermissionCode(
        permission_codes,
        userData.permission_code
      );
      if (!profile_uid || !userData.profile_type_code) {
        return null;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        return null;
      }

      if (typeof userData.must_change_password === 'boolean') {
        persistMustChangePassword(userData.must_change_password);
      }

      const { uid: _sessionUid, ...userWithoutUid } = userData;
      return {
        ...userWithoutUid,
        profile_uid,
        permission_codes,
        permission_code,
        account_id: userData.account_uid ?? userData.account_id,
        must_change_password: Boolean(userData.must_change_password),
        token
      };
    } catch (error) {
      void error;
      return null;
    }
  }, []);

  // Принудительное обновление user из /me (без учёта таймаута). Для страниц, где нужны актуальные права при первой загрузке.
  const refreshUserFromMe = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token || isTokenExpired(token)) return null;
    try {
      const updated = await updateUserData();
      if (updated) {
        setUser(updated);
        setLastPermissionCheck(Date.now());
        return updated;
      }
    } catch (e) {
      void e;
    }
    return null;
  }, [updateUserData]);

  // Функция для безопасного перенаправления на страницу логина
  const safeRedirectToLogin = () => {
    const now = Date.now();
    // Проверяем, прошло ли достаточно времени с последнего перенаправления (минимум 30 секунд)
    if (now - lastRedirectTime > 30000) {
      setLastRedirectTime(now);
      
      // Проверяем, что мы не на странице логина
      if (window.location.pathname !== '/login') {
        // Добавляем задержку перед перенаправлением и гарантируем cleanup старого таймера.
        clearRedirectTimeout();
        redirectTimeoutRef.current = setTimeout(() => {
          window.location.href = '/login';
        }, LOGIN_REDIRECT_DELAY_MS);
      }
    }
  };

  const refreshTokenSilently = useCallback(async () => {
    try {
      const refresh_token = localStorage.getItem('refresh_token');
      if (!refresh_token) {
        return false;
      }

      if (isTokenExpired(refresh_token)) {
        return false;
      }

      const access_token = await refreshAccessTokenSingleFlight();

      const userData = getUserFromToken(access_token);
      if (userData) {
        setUser({
          ...userData,
          token: access_token
        });
      }

      return true;
    } catch (error) {
      void error;
      const refresh_token = localStorage.getItem('refresh_token');
      if (!refresh_token || isTokenExpired(refresh_token)) {
        return false;
      }
      return false;
    }
  }, []);

  // Проверка сессии по токену и при необходимости запрос /me. При старте приложения пользователь восстанавливается из localStorage (см. инициализацию user/loading); verifyAuth вызывается по необходимости извне.
  const verifyAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refresh_token');
      
      if (!token) {
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        setUser(null);
        setLoading(false);
        return;
      }

      if (isTokenExpired(token)) {
        // Не разлогиниваем пользователя, если refresh_token ещё жив — сначала пробуем обновление.
        if (refreshToken && !isTokenExpired(refreshToken)) {
          const refreshed = await refreshTokenSilently();
          if (refreshed) {
            setLastTokenCheck(Date.now());
            setLoading(false);
            return;
          }
        }
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        setUser(null);
        setLoading(false);
        return;
      }

      // Проверяем, нужно ли делать запрос к /me
      // Если с последней проверки прошло менее 5 минут, используем данные из токена
      const now = Date.now();
      
      // Если lastTokenCheck не установлен или равен 0, устанавливаем его на текущее время
      if (!lastTokenCheck) {
        setLastTokenCheck(now);
      }
      
      // Если с последней проверки прошло менее 5 минут или это первая загрузка, используем данные из токена
      if (now - lastTokenCheck < 300000 || loading) {
        const userData = getUserFromToken(token);
        if (userData) {
          setUser({
            ...userData,
            token
          });
          // Обновляем время последней проверки, если это первая загрузка
          if (loading) {
            setLastTokenCheck(now);
          }
          setLoading(false);
          return;
        }
      }

      try {
        // Устанавливаем таймаут для запроса
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          controller.abort();
        }, 10000); // 10 секунд таймаут
        
        const response = await authApi.get('/me', { signal: controller.signal });
        
        clearTimeout(timeoutId);
        
        const userData = response.data;
        
        setUser({
          ...userData,
          token
        });
        setLastPermissionCheck(Date.now());
        setLastTokenCheck(Date.now()); // Обновляем время последней проверки
      } catch (error) {

        // Проверяем, был ли запрос отменен из-за таймаута
        if (error.name === 'AbortError') {
          // Пытаемся получить данные из токена
          const userData = getUserFromToken(token);
          if (userData) {
            setUser({
              ...userData,
              token
            });
            setLoading(false);
            return;
          }
        }
        
        // Получаем текущее время и проверяем refresh_token перед выкидыванием
        const currentTime = Date.now();
        const lastTokenRemoval = parseInt(localStorage.getItem('lastTokenRemoval') || '0');
        const refresh_token = localStorage.getItem('refresh_token');
        const isRefreshTokenValid = refresh_token && !isTokenExpired(refresh_token);
        
        // Проверяем, связана ли ошибка с аутентификацией
        if (error.response?.status === 401 || (error.response?.data && error.response.data.auth_error)) {
          // Выкидываем пользователя только если refresh_token действительно истек
          if (!isRefreshTokenValid) {
            // Удаляем токены не чаще чем раз в 30 секунд
            if (currentTime - lastTokenRemoval > 30000) {
              localStorage.removeItem('token');
              localStorage.removeItem('refresh_token');
              localStorage.setItem('lastTokenRemoval', currentTime.toString());
              setUser(null);
              
              // Безопасное перенаправление на страницу входа
              safeRedirectToLogin();
            } else {
              // Пытаемся получить данные из токена
              const userData = getUserFromToken(token);
              if (userData) {
                setUser({
                  ...userData,
                  token
                });
              }
            }
          } else {
            // Если refresh_token валиден, не выкидываем пользователя
            const userData = getUserFromToken(token);
            if (userData) {
              setUser({
                ...userData,
                token
              });
            }
          }
        } else {
          // Если ошибка не связана с аутентификацией, сохраняем текущего пользователя
          const userData = getUserFromToken(token);
          if (userData) {
            setUser({
              ...userData,
              token
            });
          } else {
            // Если не удалось получить данные из токена, проверяем refresh_token
            if (!isRefreshTokenValid && currentTime - lastTokenRemoval > 30000) {
              localStorage.removeItem('token');
              localStorage.removeItem('refresh_token');
              localStorage.setItem('lastTokenRemoval', currentTime.toString());
              setUser(null);
              
              // Безопасное перенаправление на страницу входа
              safeRedirectToLogin();
            }
          }
        }
      }
      
      // Убедимся, что loading всегда устанавливается в false в конце функции
      setLoading(false);
    } catch (e) {
      void e;
      setLoading(false);
    }
  };

  useEffect(() => {
    // Если пользователь уже установлен через lazy initialization, ничего не делаем
    if (user) {
      return () => {};
    }

    // Если loading уже false и пользователя нет - значит токена нет или он невалиден
    if (!loading) {
      return () => {};
    }

    // Если мы здесь - значит идет какая-то долгая операция
    // Устанавливаем таймаут для завершения loading
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 3000);
    
    return () => clearTimeout(timeout);
  }, [user, loading]);

  const login = async (credentials) => {
    try {
      clearRedirectTimeout();
      const deviceInfo = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      const response = await authApi.post('/login/json', {
        ...credentials,
        device_info: JSON.stringify(deviceInfo)
      });

      const { access_token, refresh_token, user } = response.data;

      if (!access_token || !refresh_token || !user) {
        throw new Error('Неполные данные авторизации (нет refresh_token)');
      }

      localStorage.setItem('token', access_token);
      localStorage.setItem('refresh_token', refresh_token);

      // Бэкенд в /login/json возвращает user.permissions (список кодов),
      // но фронт везде работает с permission_codes. Приводим к единому имени,
      // а для профиля `system_console` автоматически добавляем permission
      // `system_console` — иначе RouteGuard валит на /403 сразу после логина.
      const rawPermissions = Array.isArray(user.permissions)
        ? user.permissions
        : Array.isArray(user.permission_codes)
          ? user.permission_codes
          : [];
      const permission_codes = [...rawPermissions];
      if (user.profile_type === 'system_console' && !permission_codes.includes('system_console')) {
        permission_codes.push('system_console');
      }
      const permission_code = primaryPermissionCode(permission_codes, user.permission_code);

      const mustChange = Boolean(user.must_change_password);
      persistMustChangePassword(mustChange);

      const userData = {
        ...user,
        profile_type_code: user.profile_type, // Приводим к единому формату
        permission_codes,
        permission_code,
        account_id: user.account_uid ?? user.account_id,
        must_change_password: mustChange,
        token: access_token,
      };

      setUser(userData);
      setLoading(false); // Явно устанавливаем loading в false после успешного логина
      setLastPermissionCheck(Date.now());
      return userData;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  };

  const logout = useCallback(async () => {
    try {
      clearRedirectTimeout();
      await authApi.post('/logout');
    } catch (error) {
      void error;
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');

      try {
        const docMod = await import('./DocumentStatusesContext');
        docMod.invalidateStatusesCache();
        const repMod = await import('./RepairsContext');
        repMod.invalidateRepairCategoriesCache();
      } catch (err) {
        void err;
        sessionStorage.removeItem('document_statuses_cache');
        sessionStorage.removeItem('document_statuses_cache_timestamp');
        sessionStorage.removeItem('document_statuses_cache_v2');
        sessionStorage.removeItem('document_statuses_cache_v2_timestamp');
      }
      clearDocumentsCache();
      sessionStorage.removeItem('notification_stats_messages');
      sessionStorage.removeItem('notification_stats_notifications');
      sessionStorage.removeItem('push_notifications_https_warning_shown');
      sessionStorage.removeItem('auth_redirect_ts');
      sessionStorage.removeItem('document_types_cache');
      sessionStorage.removeItem(MUST_CHANGE_PASSWORD_KEY);
      setUser(null);
    }
  }, [clearRedirectTimeout]);

  refreshTokenSilentlyRef.current = refreshTokenSilently;
  logoutRef.current = logout;

  useEffect(() => {
    if (!user) return () => {};

    const tokenCheckInterval = setInterval(() => {
      const token = localStorage.getItem('token');
      const refresh_token = localStorage.getItem('refresh_token');

      if (!refresh_token || isTokenExpired(refresh_token)) {
        void logoutRef.current();
        return;
      }

      if (!token || isTokenExpired(token)) {
        void refreshTokenSilentlyRef.current().then((success) => {
          if (!success) {
            const currentRefreshToken = localStorage.getItem('refresh_token');
            if (!currentRefreshToken || isTokenExpired(currentRefreshToken)) {
              void logoutRef.current();
            }
          }
        });
      }
    }, 120000);

    return () => clearInterval(tokenCheckInterval);
  }, [user]);

  useEffect(() => {
    if (!user) return () => {};

    const checkAndRefreshToken = () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const decoded = parseJwt(token);
        if (!decoded || !decoded.exp) return;

        const expirationTime = decoded.exp * 1000;
        const currentTime = Date.now();
        const timeUntilExpiry = expirationTime - currentTime;

        if (timeUntilExpiry > 0 && timeUntilExpiry < 300000) {
          void refreshTokenSilentlyRef.current();
        }
      } catch (e) {
        void e;
      }
    };

    checkAndRefreshToken();
    const proactiveRefreshInterval = setInterval(checkAndRefreshToken, 60000);

    return () => clearInterval(proactiveRefreshInterval);
  }, [user]);

  const verifyPermissions = async () => {
    if (!user || !shouldCheckPermissions()) {
      return user;
    }

    try {
      const updatedUser = await updateUserData();
      if (updatedUser) {
        const token = localStorage.getItem('token');
        const userWithToken = {
          ...updatedUser,
          token
        };
        setUser(userWithToken);
        setLastPermissionCheck(Date.now());
        return userWithToken;
      }
      await logout();
      return null;
    } catch (error) {
      void error;
      await logout();
      return null;
    }
  };

  useEffect(() => () => clearRedirectTimeout(), [clearRedirectTimeout]);

  // Прогрев workflow-runtime кэша для всех известных типов документов
  // сразу после появления user. Нужен, чтобы synchronous-читатели
  // (print-мапперы, Search, breadcrumb до первого рендера) не падали
  // на legacy-fallback, которого больше нет. Prefetch дедуплицирован
  // в workflowPolicyCache, поэтому параллельные монтирования не создают
  // лишних запросов.
  useEffect(() => {
    if (!user) return;
    void prefetchAllWorkflowPolicies().catch(() => {});
  }, [user]);

  const value = {
    user,
    login,
    logout,
    verifyAuth,
    verifyPermissions,
    refreshUserFromMe,
    isAuthenticated: !!user,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}